import React, { createContext, useContext, useEffect, useState } from 'react';

type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';
type AccentColor = 'teal' | 'purple' | 'blue' | 'green' | 'orange' | 'pink' | 'red';

const ACCENT_COLORS: Record<AccentColor, { light: string; dark: string; hover_light: string; hover_dark: string }> = {
  teal:   { light: '#2a9d8f', dark: '#5bc4b5', hover_light: '#238578', hover_dark: '#4db3a5' },
  purple: { light: '#7c3aed', dark: '#a78bfa', hover_light: '#6d28d9', hover_dark: '#8b5cf6' },
  blue:   { light: '#2563eb', dark: '#60a5fa', hover_light: '#1d4ed8', hover_dark: '#3b82f6' },
  green:  { light: '#059669', dark: '#34d399', hover_light: '#047857', hover_dark: '#10b981' },
  orange: { light: '#ea580c', dark: '#fb923c', hover_light: '#c2410c', hover_dark: '#f97316' },
  pink:   { light: '#db2777', dark: '#f472b6', hover_light: '#be185d', hover_dark: '#ec4899' },
  red:    { light: '#dc2626', dark: '#f87171', hover_light: '#b91c1c', hover_dark: '#ef4444' },
};

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

interface ThemeContextType {
  theme: ResolvedTheme;
  themePreference: ThemePreference;
  setThemePreference: (preference: ThemePreference) => void;
  toggleTheme: () => void;
  accentColor: AccentColor;
  setAccentColor: (color: AccentColor) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [accentColor, setAccentColorState] = useState<AccentColor>(() => {
    const saved = localStorage.getItem('calendar-accent-color') as AccentColor | null;
    return saved && saved in ACCENT_COLORS ? saved : 'teal';
  });

  const setAccentColor = (color: AccentColor) => {
    setAccentColorState(color);
    localStorage.setItem('calendar-accent-color', color);
  };

  const [themePreference, setThemePreference] = useState<ThemePreference>(() => {
    const saved = localStorage.getItem('calendar-theme-preference') as ThemePreference | null;
    if (saved && ['light', 'dark', 'system'].includes(saved)) {
      return saved;
    }
    return 'system';
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    if (themePreference === 'system') {
      return getSystemTheme();
    }
    return themePreference as ResolvedTheme;
  });

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      if (themePreference === 'system') {
        setResolvedTheme(getSystemTheme());
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference]);

  // Update resolved theme when preference changes
  useEffect(() => {
    if (themePreference === 'system') {
      setResolvedTheme(getSystemTheme());
    } else {
      setResolvedTheme(themePreference);
    }
    localStorage.setItem('calendar-theme-preference', themePreference);
  }, [themePreference]);

  // Apply theme class to document with smooth transition
  useEffect(() => {
    const root = document.documentElement;
    // Add transitioning class before changing theme
    root.classList.add('theme-transitioning');
    if (resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    // Remove transitioning class after transitions complete
    const timer = setTimeout(() => {
      root.classList.remove('theme-transitioning');
    }, 350);
    return () => clearTimeout(timer);
  }, [resolvedTheme]);

  // Apply accent color CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const colors = ACCENT_COLORS[accentColor];
    const isDark = resolvedTheme === 'dark';
    const primary = isDark ? colors.dark : colors.light;
    const hover = isDark ? colors.hover_dark : colors.hover_light;
    const rgb = hexToRgb(primary);

    root.style.setProperty('--accent-primary', primary);
    root.style.setProperty('--accent-hover', hover);
    root.style.setProperty('--accent-subtle', `rgba(${rgb}, ${isDark ? '0.12' : '0.08'})`);
    root.style.setProperty('--accent-drag', `rgba(${rgb}, 0.35)`);
  }, [accentColor, resolvedTheme]);

  const toggleTheme = () => {
    // Simple two-state toggle (light ↔ dark), skipping system
    // System preference is still available via settings page
    setThemePreference(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{
      theme: resolvedTheme,
      themePreference,
      setThemePreference,
      toggleTheme,
      accentColor,
      setAccentColor,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}